import {
  LaunchTask,
  WorkStatus,
  BuildRequestStatus,
  PackageBuildInfo,
  PackageType,
  UpdateReviewStatusCommand,
  UpdateReviewStatusCommandOutput,
  UpdateReviewStatusCommandInput,
  UpdatePackageBuildInfoCommand,
  UpdatePackageBuildInfoCommandInput,
  GetEntityBuildRequestInfoCommandOutput,
  EntityType,
  GetPackagePermissionsCommand,
  PackagePermissionResult,
  PermissionEnforcement,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import {
  Badge,
  Button,
  Link,
  Popover,
  SpaceBetween,
} from '@amzn/awsui-components-console';
import { useEffect, useRef, useState, ReactNode } from 'react';
import styles from './Packages.module.sass';
import { useAuthState } from '../../../../authentication/context';
import { useInterval } from '../../useInterval';
import { AsyncErrorBar } from '../../../../components/AsyncErrorBar';
import { useApiErrors } from '../../../../components/ApiErrorContext';
import { BuildStatusBadge } from '../BuildStatusBadge';
import { PublishConfirmer } from '../../../../PublishConfirmer';
import { PublishNotice } from '../../../../PublishNotice';
import { Warning } from '../../../../Warning';
import { CrossVenueWarning } from '../../../../CrossVenueWarning';
import { FUSION_ASSISTANT_PROMPT_CROSS_VENUE_PREFIX } from '../../../../fusionAssistant/constants';
import { ProtoPBI } from './ProtoPBI';
import { ListUpdater } from './ListUpdater';
import { PackageForm } from './PackageForm';
import { PackagesHeader } from './PackagesHeader';
import { callApi } from '../../../../client';
import { BuildIdsMapComponent } from '../BuildIdsMapComponent';
import { shouldShowApiRefDependencyWarning } from './packageWarnings';
import { validatePackages } from './packageValidation';
import { isAutoMergeEligible } from './autoMergeEligibility';
import { FUG_PERMISSIONS_URL } from './permissionConstants';

const { SUBMIT_FAILED, CANCELED, FAILED, DONE, SUBMIT_SKIPPED } =
  BuildRequestStatus;
const {
  PUBLISH_SCHEDULED,
  DELETED,
  TASK_RECEIVED,
  MANIFEST_BUILT,
  REVIEW_APPROVED,
  RESOLVED,
  SHIPPED,
  GROUPED,
} = WorkStatus;
const terminalBuildStatuses: BuildRequestStatus[] = [
  SUBMIT_FAILED,
  CANCELED,
  FAILED,
  DONE,
  SUBMIT_SKIPPED,
];
const terminalWorkStatuses: WorkStatus[] = [
  PUBLISH_SCHEDULED,
  DELETED,
  TASK_RECEIVED,
  MANIFEST_BUILT,
  RESOLVED,
  SHIPPED,
  GROUPED,
];

type PackageList = PackageBuildInfo[];

interface PackagesProps {
  // Starter list of packages to display
  task: LaunchTask;

  // Fired when any field is typed in
  onChange?: (items: PackageList) => void;

  // Fired when the user clicks Save
  onSave?: (items: PackageList) => void;

  // Should be fired when the future cancel button is clicked
  onCancel?: (items: PackageList) => void;

  editable?: boolean;

  onPoll?: (workStatus: WorkStatus) => void;

  publish?: boolean;

  onPublishing?: () => void;

  // Caller-supplied submit handler. When provided, Packages calls this
  // instead of UpdatePackageBuildInfoCommand. When absent, existing behaviour preserved.
  onSubmitPackageBuildInfo?: (
    packageBuildInfoList: PackageBuildInfo[]
  ) => Promise<any>;

  // Caller-supplied poll handler. When provided, useInterval calls this.
  // When absent, polling is disabled.
  onPollStatus?: () => Promise<GetEntityBuildRequestInfoCommandOutput>;

  // When provided, passed to UpdateReviewStatusCommand calls.
  // Use 'GROUP' for group entities, undefined/omitted for tasks.
  entityType?: EntityType;
}

const normalizePackages = (packages: PackageBuildInfo[]): ProtoPBI[] =>
  packages.map((p) => {
    const packageType = Object.values(PackageType).includes(p.packageType)
      ? p.packageType
      : PackageType.OTHER;
    return {
      id: crypto.randomUUID(),
      ...p,
      packageType,
      // Drop any destination branch carried on an auto-merge-ineligible type (legacy data or an
      // earlier MCP submission): auto-merge never uses it, the field is disabled for these types,
      // and the API rejects it — so normalize it away on load rather than show an un-clearable,
      // disabled field holding a value that would fail submission.
      publishingBranch: isAutoMergeEligible(packageType)
        ? p.publishingBranch ?? ''
        : '',
    };
  });

const formatAsyncError = (error: string | null): string | null => {
  if (!error) return null;
  if (error.toLowerCase().includes('conflicting request(s)')) {
    const match = /\[[^\]]+\]/.exec(error);
    const buildIds = match ? match[0] : null;
    return buildIds
      ? `A conflicting build is in progress on the version set. Your build may retry automatically, or you can re-submit your packages once the conflicting build completes. Conflicting build(s): ${buildIds}`
      : 'A conflicting build is in progress on the version set. Your build may retry automatically, or you can re-submit your packages once the conflicting build completes.';
  }
  return error;
};

export const Packages = (props: PackagesProps) => {
  const [polling, setPolling] = useState(false);
  const [asyncError, setAsyncError] = useState<string | null>(null);
  const { isAuthenticated, token } = useAuthState();
  const { pushError } = useApiErrors();
  // True while a Submit request is in flight. Used to hide "Add package"
  // and put Submit into a disabled, spinner-loading state so users can't
  // double-submit before workStatus advances to PACKAGE_SUBMITTED_BY_WRITER.
  const [submitting, setSubmitting] = useState(false);
  // Current state of the package list, including modifications
  const [items, setItems] = useState<ProtoPBI[]>(
    normalizePackages(props.task.packageBuildInfoList ?? [])
  );

  const [publishNoticeVisible, setPublishNoticeVisible] = useState(false);

  const [workStatus, setWorkStatus] = useState(props.task.workStatus);
  const [buildStatus, setBuildStatus] = useState(props.task.buildRequestStatus);
  // Display validation errors for any textbox
  const [validate, setValidate] = useState(true);

  const [buildId, setBuildId] = useState(props.task.buildRequestId);
  const [buildIdsMap, setBuildIdsMap] = useState(props.task.buildIdsMap);

  const [taskReceived, setTaskReceived] = useState(
    props.task.workStatus === 'TASK_RECEIVED'
  );
  // Should the fields be editable
  const [editing, setEditing] = useState(props.editable ?? false);
  // const [finalizing, setFinalizing] = useState(manifestBuilt);

  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState<ReactNode>('');
  const [headerMessage, setHeaderMessage] = useState<string>('');

  const [sharesAcrossVenues, setSharesAcrossVenues] = useState<boolean>(
    props.task.sharesPackageAcrossVenues === true
  );
  const lastSeenLaunchIdRef = useRef<string>(props.task.launchId);

  // Permission check state
  const [permissionResults, setPermissionResults] = useState<
    Map<string, PackagePermissionResult>
  >(new Map());
  const [checkingPermissions, setCheckingPermissions] = useState(false);

  const isAllAllowed = (result: PackagePermissionResult): boolean => {
    const perms = result.permissions ?? [];
    return perms.length > 0 && perms.every((p) => p.status === 'ALLOWED');
  };

  const checkPermissions = async () => {
    if (!token) return;

    const packageNames = items
      .map((p) => p.packageName)
      .filter(
        (name): name is string =>
          !!name &&
          !isAllAllowed(
            permissionResults.get(name) ?? {
              packageName: name,
              permissions: [],
            }
          )
      );

    if (packageNames.length === 0) return;

    setCheckingPermissions(true);
    try {
      const response = await callApi(
        new GetPackagePermissionsCommand({
          packageNames,
          entityId: props.task.launchId,
          entityType: props.entityType ?? 'TASK',
        }),
        token
      );
      if (response.results) {
        const newResults = new Map(permissionResults);
        for (const result of response.results) {
          newResults.set(result.packageName, result);
        }
        setPermissionResults(newResults);
      }
    } catch (err: any) {
      pushError('Permission Check Failed', err?.message || String(err));
    } finally {
      setCheckingPermissions(false);
    }
  };
  useEffect(() => {
    if (!props.publish) return;
    props.onPublishing?.();
    publish();
  }, [props.publish]);

  useEffect(() => {
    const buildableTypes: PackageType[] = [
      PackageType.GUIDE_OR_HELP_PANEL,
      PackageType.SERVICE_LANDING_PAGE,
      PackageType.API_REFERENCE_SMITHY,
      PackageType.API_REFERENCE_STRUT,
      PackageType.API_REFERENCE_REEF,
    ];
    // Warn if packages were submitted but none produce documentation (a guide/help panel,
    // service landing page, or API reference). Such a build produces nothing to publish.
    const hasBuildableType = items.some((p) =>
      buildableTypes.includes(p.packageType as PackageType)
    );
    const hasAPopulatedPackage = items.some(
      (p) => p.packageName && p.packageType && p.branch && p.commitId
    );
    const showNoBuildableTypeWarning =
      items.length > 0 && hasAPopulatedPackage && !hasBuildableType;

    const showZonbookWarning =
      items.some((p) => p.packageType === PackageType.GUIDE_OR_HELP_PANEL) &&
      !items.some((p) => p.packageType === PackageType.CHECKLIST);

    const hasService = items.some(
      (p) => p.packageType === PackageType.SERVICE_MODEL
    );
    const hasDoc = items.some((p) => p.packageType === PackageType.DOC_MODEL);
    const hasC2J = items.some((p) => p.packageType === PackageType.C2J);
    const hasExternal = items.some(
      (p) => p.packageType === PackageType.EXTERNAL_MODEL
    );
    const showApiRefDependencyWarning =
      shouldShowApiRefDependencyWarning(items);

    const showReefWarning =
      items.some((p) => p.packageType === PackageType.API_REFERENCE_REEF) &&
      (!hasService || !hasDoc || !hasC2J);
    const showSmithyWarning =
      items.some((p) => p.packageType === PackageType.API_REFERENCE_SMITHY) &&
      (!hasService || !hasDoc || !hasExternal);

    if (showNoBuildableTypeWarning) {
      setShowWarning(true);
      setHeaderMessage('No buildable package type selected');
      setWarningMessage(
        'At least one package must be a Guide or Help Panel, Service docs landing ' +
          'page, or APIDoc (Smithy, Strut, or Reef). Without one of these types, the ' +
          'build produces no documentation to publish.'
      );
    } else if (showZonbookWarning) {
      setShowWarning(true);
      setHeaderMessage('Recommended: add your Checklist package');
      setWarningMessage(
        <>
          This build doesn&apos;t include a Checklist package. A Checklist
          isn&apos;t strictly required, but if your guide has one, we recommend
          specifying it here so your published content is complete. For details
          on why, and help identifying which packages to include in an FLC
          build, see{' '}
          <Link
            external
            href='https://alpha.www.docs.aws.a2z.com/fusion/latest/userguide/flc-using-flc.html#flc-discover-dependencies'
          >
            Discover which packages to include
          </Link>{' '}
          in the <i>Fusion User Guide</i>.
        </>
      );
    } else if (showReefWarning) {
      setShowWarning(true);
      setHeaderMessage('Have a APIDoc package?');
      setWarningMessage(
        'Reef PackageType needs one or more service model, doc model and C2J packages'
      );
    } else if (showSmithyWarning) {
      setShowWarning(true);
      setHeaderMessage('Have a APIDoc package?');
      setWarningMessage(
        'Smithy PackageType needs one or more service model, doc model and External model packages'
      );
    } else if (showApiRefDependencyWarning) {
      // Kept last so the more specific Reef/Smithy warnings (which require C2J vs
      // External Model respectively) take priority when an ApiDoc package is present.
      // This generic chain warning then covers the remaining case: partial API Reference
      // chains with no ApiDoc package yet (e.g., a lone Service Model), which the
      // Reef/Smithy warnings don't fire for.
      setShowWarning(true);
      setHeaderMessage('API Reference dependency chain incomplete');
      setWarningMessage(
        'API Reference builds require the full dependency chain: Service Model, Doc Model, ' +
          'External Model (or C2J), and an ApiDoc package. Please add the missing package type(s).'
      );
    } else {
      setShowWarning(false);
    }
  }, [items]);

  useEffect(() => {
    setTaskReceived(workStatus === TASK_RECEIVED);
    console.log('Approved?', workStatus === REVIEW_APPROVED);
  }, [workStatus]);

  useEffect(() => {
    console.log('buildStatus', buildStatus, 'workStatus', workStatus);
    const buildTerminal =
      !buildStatus || terminalBuildStatuses.includes(buildStatus);
    const workTerminal =
      workStatus && terminalWorkStatuses.includes(workStatus);
    const poll = !(buildTerminal && workTerminal);
    setPolling(poll);
  }, [buildStatus, workStatus]);

  // Track the last packageBuildInfoList we synced from props so we only reset
  // `items` when the parent supplies genuinely new data — not when the task
  // reference changes but packageBuildInfoList is the same stale content.
  // This preserves local edits across failed submits and prevents stale resets
  // after a successful submit (where the in-memory parent hasn't refetched yet).
  const lastSyncedPBIRef = useRef<string>(
    JSON.stringify(props.task.packageBuildInfoList ?? [])
  );

  useEffect(() => {
    const incomingPBI = JSON.stringify(props.task.packageBuildInfoList ?? []);
    if (incomingPBI !== lastSyncedPBIRef.current) {
      lastSyncedPBIRef.current = incomingPBI;
      setItems(normalizePackages(props.task.packageBuildInfoList ?? []));
    }
    setWorkStatus(props.task.workStatus);
    setBuildStatus(props.task.buildRequestStatus);
    setBuildId(props.task.buildRequestId);

    const propsFlag = props.task.sharesPackageAcrossVenues === true;
    if (props.task.launchId !== lastSeenLaunchIdRef.current) {
      lastSeenLaunchIdRef.current = props.task.launchId;
      setSharesAcrossVenues(propsFlag);
    } else if (propsFlag) {
      setSharesAcrossVenues(true);
    }
  }, [props.task]);

  useEffect(() => {
    const nowEdit = taskReceived && (props.editable ?? false);
    console.log('Now edit?', nowEdit);
    setEditing(nowEdit);
  }, [props, taskReceived]);

  const update: ListUpdater =
    (index: number) =>
    (prop: string) =>
    ({ detail }) => {
      const { value } = 'value' in detail ? detail : detail.selectedOption;
      const updatedItem = { ...items[index], [prop]: value };

      // When switching to a package type that doesn't support post-conference auto-merge,
      // clear any Destination branch so we never carry one for an ineligible type (the API
      // rejects that, and the field is disabled for these types in the form).
      if (
        prop === 'packageType' &&
        !isAutoMergeEligible(value as PackageType | '')
      ) {
        updatedItem.publishingBranch = '';
      }

      setItems([
        // Leave other packages the same
        ...items.slice(0, index),

        // Update only the changed prop (plus any dependent clearing) of the specified package
        updatedItem,

        // Leave other packages the same
        ...items.slice(index + 1),
      ]);
    };

  /// Fire onChange event when a field has been changed
  useEffect(() => props.onChange?.(items as PackageList), [items]);

  /**
   * save: Deactivate editing and, on a successful submit, fire the onSave event.
   * TODO: Temporarily setValidate(true) before saving
   */
  const submit = async () => {
    if (!token) {
      alert(
        'Your session has expired. Please refresh the page to re-authenticate.'
      );
      return;
    }

    // Pre-submission validation: catch input mistakes on the front end and show a
    // friendly, actionable message instead of the raw Coral model validation error
    // (e.g. the "...failed to satisfy constraint: [0-9a-f]{40}" commit-ID error).
    // Returning early here — before locking the fields (setEditing(false) below) —
    // also leaves the fields editable so the user can fix the problem in place.
    const validationErrors = validatePackages(items);
    if (validationErrors.length > 0) {
      pushError(
        validationErrors.length === 1
          ? 'Please fix this before submitting'
          : 'Please fix the following before submitting',
        validationErrors.join('\n')
      );
      return;
    }

    console.log('Saving', items);
    setSubmitting(true);
    setEditing(false);

    // Trim whitespace from package names
    const packageBuildInfoList: PackageBuildInfo[] = items.map((pkg) => ({
      ...pkg,
      packageName: pkg.packageName.trim(),
      branch: pkg.branch.trim(),
      // Trim commitId too: validatePackages trims before checking, so a
      // whitespace-padded 40-hex commit passes validation — it must be trimmed
      // here as well or the untrimmed value is sent and the service rejects it
      // with the raw Coral [0-9a-f]{40} error this validation exists to prevent.
      commitId: pkg.commitId.trim(),
      packageType: pkg.packageType === '' ? PackageType.OTHER : pkg.packageType,
      // Treat a blank publishing branch as "not set" — omit it so the API
      // receives undefined rather than an empty string.
      publishingBranch: pkg.publishingBranch?.trim()
        ? pkg.publishingBranch.trim()
        : undefined,
    }));

    try {
      let response: { $metadata: { httpStatusCode?: number } };
      if (props.onSubmitPackageBuildInfo) {
        response = await props.onSubmitPackageBuildInfo(packageBuildInfoList);
      } else {
        const updatePackageBuildInfoInput: UpdatePackageBuildInfoCommandInput =
          {
            launchId: props.task.launchId,
            packageBuildInfoList: packageBuildInfoList,
          };
        response = await callApi(
          new UpdatePackageBuildInfoCommand(updatePackageBuildInfoInput),
          token
        );
      }

      if (response.$metadata.httpStatusCode == 200) {
        setWorkStatus(WorkStatus.PACKAGE_SUBMITTED_BY_WRITER);

        const warnings: string[] = (response as any).warnings ?? [];
        if (warnings.length > 0) {
          pushError(
            'Submission succeeded with warnings',
            `For the following package(s): ${warnings.join(
              ', '
            )} — your package's branch and commit could not be validated because FLC was not granted the appropriate permissions. ` +
              `Upcoming builds may fail or produce unexpected results if branch or commit are invalid. ` +
              `See: ${FUG_PERMISSIONS_URL}`,
            'warning'
          );
        }

        // Fire onSave only on a successful submit — a failed submit (non-200
        // or thrown) must not signal a save to onSave listeners.
        props.onSave?.(items as PackageList);
      } else {
        console.error(
          'Unexpected response status code:',
          response.$metadata.httpStatusCode
        );
        pushError(
          'Package Submit Failed',
          `The service returned an unexpected status code (${
            response.$metadata.httpStatusCode ?? 'unknown'
          }). Please try again.`
        );
        // Re-enable the fields for the same reason as the catch block below: a
        // non-200 response does not throw, so without this the fields stay
        // locked (workStatus is still TASK_RECEIVED) with no way to correct and
        // resubmit.
        setEditing(true);
      }
    } catch (err: any) {
      pushError('Package Submit Failed', err?.message || String(err));
      // Re-enable the fields so the user can correct and re-submit in place.
      // submit() set editing=false above; on a failed submit the workStatus stays
      // TASK_RECEIVED, so the effect that recomputes `editing` (deps: props,
      // taskReceived) never re-fires and the fields would otherwise stay locked.
      // This covers any submission failure that reaches the service (front-end
      // validation errors are already caught before we lock the fields).
      setEditing(true);
    } finally {
      setSubmitting(false);
      setPolling(true);
    }
  };

  useInterval(() => {
    void (async () => {
      if (token && polling && props.onPollStatus) {
        try {
          const res: GetEntityBuildRequestInfoCommandOutput =
            await props.onPollStatus();

          if (res.$metadata.httpStatusCode == 200) {
            console.log('buildRequest', res);
            const buildRequestStatus = res.buildRequestInfo.buildRequestStatus;
            const buildSubmitError = res.buildRequestInfo.buildSubmitError;
            const buildRequestId = res.buildRequestInfo.buildRequestId;
            const buildInfoMap = res.buildRequestInfo.buildIdsMap;
            setBuildStatus(buildRequestStatus);
            setWorkStatus(res.workStatus);
            if (res.sharesPackageAcrossVenues === true) {
              setSharesAcrossVenues(true);
            }
            // Only show buildSubmitError when buildRequestStatus is SUBMIT_FAILED
            setAsyncError(
              buildRequestStatus === BuildRequestStatus.SUBMIT_FAILED
                ? buildSubmitError ?? null
                : null
            );
            setBuildId(buildRequestId);
            setBuildIdsMap(buildInfoMap);
            props.onPoll?.(res.workStatus);
          } else {
            console.error(
              'Unexpected response status code:',
              res.$metadata.httpStatusCode
            );
          }
        } catch (err: any) {
          pushError('Polling Error', err?.message || String(err));
          setPolling(false);
        }
      }
    })();
  }, 2000);

  /**
   * addPackage: append an empty package to the list `items`
   */
  const addPackage = () =>
    setItems(
      items.concat({
        id: crypto.randomUUID(),
        packageName: '',
        packageType: '',
        branch: '',
        commitId: '',
        publishingBranch: '',
      })
    );

  const [confirming, setConfirming] = useState(false);

  const reject = async () => {
    if (!token) {
      alert(
        'Your session has expired. Please refresh the page to re-authenticate.'
      );
      return;
    }
    const updateReviewInput: UpdateReviewStatusCommandInput = {
      launchId: props.task.launchId,
      reviewStatus: 'REVIEW_REJECTED',
      entityType: props.entityType,
    };
    try {
      const response: UpdateReviewStatusCommandOutput = await callApi(
        new UpdateReviewStatusCommand(updateReviewInput),
        token
      );

      if (response.$metadata.httpStatusCode == 200) {
        setWorkStatus(WorkStatus.REVIEW_REJECTED);
      } else {
        console.error(
          'Unexpected response status code:',
          response.$metadata.httpStatusCode
        );
      }
    } catch (err: any) {
      pushError('Reject Failed', err?.message || String(err));
    } finally {
      setPolling(true);
    }
  };

  const publish = () => {
    if (!token) {
      alert(
        'Your session has expired. Please refresh the page to re-authenticate.'
      );
      return;
    }
    setConfirming(true);
  };

  const publishConfirmed = async () => {
    setConfirming(false);
    setEditing(false);
    if (!token)
      return alert(
        'Your session has expired. Please refresh the page to re-authenticate.'
      );
    const updateReviewInput: UpdateReviewStatusCommandInput = {
      launchId: props.task.launchId,
      reviewStatus: 'REVIEW_APPROVED',
      entityType: props.entityType,
    };
    try {
      const response: UpdateReviewStatusCommandOutput = await callApi(
        new UpdateReviewStatusCommand(updateReviewInput),
        token
      );

      if (response.$metadata.httpStatusCode == 200) {
        setWorkStatus(WorkStatus.REVIEW_APPROVED);
        setPublishNoticeVisible(true);
      } else {
        console.error(
          'Unexpected response status code:',
          response.$metadata.httpStatusCode
        );
      }
    } catch (err: any) {
      pushError('Publish Failed', err?.message || String(err));
    } finally {
      setPolling(true);
    }
  };
  console.log('buildStatus', buildStatus, 'workStatus', workStatus);

  return (
    <>
      <SpaceBetween size={'m'}>
        <AsyncErrorBar
          error={formatAsyncError(asyncError)}
          header={
            asyncError?.toLowerCase().includes('conflicting request(s)')
              ? 'Conflicting Build Request'
              : asyncError?.includes('Config file parsing failed')
              ? 'Config File Parsing Error'
              : 'Build Submit Error'
          }
        />
        <div>
          {props.editable && workStatus && (
            <PackagesHeader
              polling={polling}
              workStatus={workStatus}
              onPublishClick={publish}
              reject={reject}
              endpoint={props.task.reviewEndpoint}
            />
          )}
        </div>
        <div>
          {buildStatus && (
            <div className={styles.status}>
              {buildStatus === SUBMIT_SKIPPED &&
              workStatus !== TASK_RECEIVED ? (
                /* SUBMIT_SKIPPED means no build ran; while the entity's work
                   status is still advancing past TASK_RECEIVED, surface a
                   transitional "Updating..." state instead of "Updated". */
                <Badge color='blue'>Status: Updating...</Badge>
              ) : (
                <BuildStatusBadge status={buildStatus} />
              )}
            </div>
          )}
          <BuildIdsMapComponent buildsMap={buildIdsMap ?? null} />
        </div>
        {/* Add package, Check Permissions, and Submit sit together just above
            the package rows so writers act on the list in context instead of
            scrolling back up to a top-of-form action cluster. Left-to-right
            order: Add package -> Check Permissions -> Submit. While a submit is
            in flight, Add package and Check Permissions hide and Submit shows a
            disabled loading state to prevent a double-submit. */}
        {props.editable && taskReceived && (
          <div>
            <SpaceBetween size='xs' direction='horizontal'>
              {!submitting && (
                <Button
                  variant='normal'
                  iconName='add-plus'
                  onClick={addPackage}
                >
                  Add package
                </Button>
              )}
              {!submitting && (
                <Button
                  variant='normal'
                  onClick={checkPermissions}
                  loading={checkingPermissions}
                  disabled={checkingPermissions}
                >
                  Check Permissions
                </Button>
              )}
              <Button
                variant='primary'
                onClick={submit}
                disabled={submitting}
                loading={submitting}
              >
                Submit
              </Button>
            </SpaceBetween>
          </div>
        )}
        {items.map((item, i) => (
          <div key={item.id}>
            <PackageForm
              update={update(i)}
              item={item}
              editing={editing}
              validate={validate}
              remove={() =>
                setItems((prev) => [...prev.slice(0, i), ...prev.slice(i + 1)])
              }
              onCommitIdFetched={(commitId) => {
                setItems((prev) => [
                  ...prev.slice(0, i),
                  { ...prev[i], commitId },
                  ...prev.slice(i + 1),
                ]);
              }}
            />
            {item.packageName && permissionResults.has(item.packageName) && (
              <div style={{ marginTop: '4px', marginLeft: '8px' }}>
                <SpaceBetween size='xs' direction='horizontal'>
                  {isAllAllowed(permissionResults.get(item.packageName)!) && (
                    <Badge color='green'>Permissions: OK</Badge>
                  )}
                  {permissionResults
                    .get(item.packageName)!
                    .permissions?.some(
                      (p) =>
                        p.status === 'DENIED' &&
                        p.enforcement === PermissionEnforcement.BLOCKING
                    ) && (
                    <Popover
                      header='Blocking — build will fail'
                      content={
                        <SpaceBetween size='xs'>
                          <span>
                            The listed account/version set cannot access this
                            package. The build will fail until access is
                            granted.
                          </span>
                          <Link external href={FUG_PERMISSIONS_URL}>
                            Permissions guidance
                          </Link>
                        </SpaceBetween>
                      }
                      triggerType='text'
                      size='medium'
                    >
                      <Badge color='red'>
                        Blocking:{' '}
                        {permissionResults
                          .get(item.packageName)!
                          .permissions?.filter(
                            (p) =>
                              p.status === 'DENIED' &&
                              p.enforcement === PermissionEnforcement.BLOCKING
                          )
                          .map((p) => `${p.actor} (${p.operation})`)
                          .join(', ')}
                      </Badge>
                    </Popover>
                  )}
                  {permissionResults
                    .get(item.packageName)!
                    .permissions?.some(
                      (p) =>
                        p.status === 'DENIED' &&
                        p.enforcement === PermissionEnforcement.NON_BLOCKING
                    ) && (
                    <Popover
                      header='Non-blocking — build may still succeed'
                      content={
                        <SpaceBetween size='xs'>
                          <span>
                            Your package&apos;s branch and commit could not be
                            validated because FLC was not granted the
                            appropriate permissions. The build can still
                            proceed.
                          </span>
                          <Link external href={FUG_PERMISSIONS_URL}>
                            Permissions guidance
                          </Link>
                        </SpaceBetween>
                      }
                      triggerType='text'
                      size='medium'
                    >
                      <Badge color='blue'>
                        Non-blocking:{' '}
                        {permissionResults
                          .get(item.packageName)!
                          .permissions?.filter(
                            (p) =>
                              p.status === 'DENIED' &&
                              p.enforcement ===
                                PermissionEnforcement.NON_BLOCKING
                          )
                          .map((p) => `${p.actor} (${p.operation})`)
                          .join(', ')}
                      </Badge>
                    </Popover>
                  )}
                  {permissionResults
                    .get(item.packageName)!
                    .permissions?.some((p) => p.status === 'UNKNOWN') &&
                    !permissionResults
                      .get(item.packageName)!
                      .permissions?.some((p) => p.status === 'DENIED') && (
                      <Popover
                        header='Could not verify'
                        content={
                          <span>
                            A transient error prevented verification. The build
                            may still succeed. Try clicking &quot;Check
                            Permissions&quot; again later.
                          </span>
                        }
                        triggerType='text'
                        size='medium'
                      >
                        <Badge color='blue'>
                          Could not verify — proceed with caution
                        </Badge>
                      </Popover>
                    )}
                </SpaceBetween>
              </div>
            )}
          </div>
        ))}
        {sharesAcrossVenues && (
          <CrossVenueWarning
            fusionContext={{
              ...(props.entityType === 'GROUP'
                ? { groupId: props.task.launchId }
                : { launchId: props.task.launchId }),
              promptPrefix: FUSION_ASSISTANT_PROMPT_CROSS_VENUE_PREFIX,
              packages: items
                .filter((p) => p.packageName)
                .map((p) => ({
                  packageName: p.packageName,
                  commitId: p.commitId || undefined,
                })),
            }}
          />
        )}
        {showWarning && (
          <Warning header={headerMessage} message={warningMessage} />
        )}
      </SpaceBetween>
      <PublishConfirmer
        launchId={props.task.launchId}
        onConfirm={publishConfirmed}
        onDismiss={() => setConfirming(false)}
        visible={confirming}
      />
      <PublishNotice
        launchId={props.task.launchId}
        visible={publishNoticeVisible}
        onDismiss={() => setPublishNoticeVisible(false)}
      />
    </>
  );
};
